import { ReferentialKey } from 'api/referential';
import { BranchCode, BorrowerContractualizationResponse, StepIdentifier, StepStatus, SubscriptionStepDto } from 'api/risk';
import { useAppDispatch } from 'app/hooks';
import { StepRouteSelectorTooltip } from 'features/subscription-process/navigation/Steps/StepRouteSelectorTooltip';
import { NsKey } from 'enums/NsKey';
import { TimelineButton } from 'features/activity-feed';
import { getMultipleReferentialsByKeys } from 'features/referentials/slices/getMultipleReferentialsByKeysThunk';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { SubscriptionProcessSkeleton } from './components/SubscriptionProcessSkeleton';
import { useSubscriptionProcessActions } from './contexts/SubscriptionProcessContext';
import { useBorrowerRiskData, useContractualizationData, useSubscriptionProcessState } from './contexts/selectors/selectors';
import { buildRoute, getStepRouteByStepIdentifierString, RouteConfigItem } from './utils/getRouteByStepIdentifier';
import { useTimelineNotifications } from 'shared/signalR/hooks/useTimelineNotifications';
import { NoteButton } from 'features/note-view/components/NoteButton';
import { ReferenceDocumentButtons } from './components/ReferenceDocumentButtons';
import type { InsuredOption } from 'features/note-view/components/NoteInsuredSelector';
import { isFileGroupSwitch } from './utils/fileGroupSwitch';

export const SubscriptionProcess: React.FC = () => {
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.Borrower])

  const { fetchSubscriptionProcess, fetchBorrowerProject, setUnreadHistoryCount, reset } = useSubscriptionProcessActions()
  const { folderIdentifier, steps, branchCode, isProcessCompleted, isLoading, unreadHistoryCount } = useSubscriptionProcessState()
  const borrowerRiskData = useBorrowerRiskData()
  const contractualizationData = useContractualizationData()
  const { riskId, borrowerProjectId, borrowerId, quoteId } = useParams<{
    riskId?: string;
    borrowerProjectId?: string;
    borrowerId?: string;
    quoteId?: string;
  }>();

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Référentiels du tunnel emprunteur : chargés ici et non sur la seule page de profilage,
  // car on peut entrer directement en tarification ou contractualisation (recherche globale,
  // fiche personne, F5) — sans ce fetch, les listes (type de prospect, type de prêt...) restent vides.
  useEffect(() => {
    if (branchCode === BranchCode.Borrower) {
      dispatch(getMultipleReferentialsByKeys([
        ReferentialKey.ProjectType,
        ReferentialKey.InsuranceProject,
        ReferentialKey.ProspectType,
        ReferentialKey.LoanType,
        ReferentialKey.InterestRateType,
        ReferentialKey.DefermentType
      ]));
    }
  }, [branchCode, dispatch]);

  // Verrouillage de navigation (emprunteur) : dès qu'un devis existe, l'utilisateur ne peut
  // plus revenir modifier le projet (Création du dossier) ni le comparateur (Tarificateur).
  // On bloque le stepper et on redirige vers la contractualisation.
  const isBorrowerNavigationLocked = useMemo(() => {
    if (branchCode !== BranchCode.Borrower || contractualizationData?.type !== 'EMP') {
      return false;
    }
    const empData =
      contractualizationData as { type: 'EMP' } & BorrowerContractualizationResponse;
    return !!empData.quoteId;
  }, [branchCode, contractualizationData]);

  const isOnContractualisation = location.pathname.includes('/contractualisation/');

  // Track previous IDs to detect simulation change
  const prevIdsRef = useRef<{ riskId?: string; borrowerProjectId?: string }>({});

  // SignalR connection for timeline notifications
  const { memoizedConnect, memoizedDisconnect } = useTimelineNotifications(
    riskId,
    (notification) => {
      setUnreadHistoryCount(notification.unreadCount);
    }
  );

  // Connect to SignalR when riskId is available
  useEffect(() => {
    if (riskId) {
      memoizedConnect();
    }
    return () => {
      memoizedDisconnect();
    };
  }, [riskId, memoizedConnect, memoizedDisconnect]);

  useEffect(() => {
    const id = borrowerId || riskId;
    const prevRiskId = prevIdsRef.current.riskId;
    const prevBorrowerProjectId = prevIdsRef.current.borrowerProjectId;

    // Detect simulation change: reset context if IDs have changed
    const hasSimulationChanged =
      (prevRiskId && id && prevRiskId !== id) ||
      (prevBorrowerProjectId && borrowerProjectId && prevBorrowerProjectId !== borrowerProjectId);

    // Switch between the files of a group (file tabs of the contractualisation): the page stays
    // on screen until the other file is loaded, instead of going back to the skeleton.
    const keepContent = isFileGroupSwitch(location.state);

    if (hasSimulationChanged && !keepContent) {
      reset();
    }

    // Update refs for next comparison
    prevIdsRef.current = { riskId: id, borrowerProjectId };

    if (id) {
      fetchSubscriptionProcess(id, quoteId, borrowerProjectId, keepContent);
    }

    if (!id && borrowerProjectId) {
      fetchBorrowerProject(borrowerProjectId);
    }
  // location.state is read, not watched: it only qualifies the navigation that changed the path.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowerProjectId, borrowerId, riskId, location.pathname, quoteId, fetchSubscriptionProcess, fetchBorrowerProject, reset]);

  const getStepRouteConfigs = useCallback((step: SubscriptionStepDto, branchCode: BranchCode) => {
    // Une sous-étape « Pending » n'est pas encore atteignable (même sémantique que le
    // disabled de l'étape principale) : elle reste visible dans le fil d'ariane mais ne
    // doit pas être proposée à la navigation.
    const reachableSubSteps = step.subSteps.filter(s => s.status !== StepStatus.Pending);
    const allIdentifiers = [step.identifier, ...reachableSubSteps.map(s => s.identifier)];
    const routeConfigs = allIdentifiers
      .map(identifier => getStepRouteByStepIdentifierString(branchCode, identifier))
      .filter((config): config is RouteConfigItem => config !== null);
    // Several sub-steps can lead to the same page (PJ: the eligibility is settled on the
    // company page): one entry per page.
    return routeConfigs.filter((config, index) =>
      routeConfigs.findIndex(other => other.route === config.route && other.label === config.label) === index
    );
  }, []);

  const getStepClasses = useCallback((status: string) => {
    const baseClasses = "w-3 h-3 border-2 absolute rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300";
    switch (status) {
      case StepStatus.Completed:
        return `${baseClasses} bg-primary border-primary text-primary`;
      case StepStatus.Current:
        return `${baseClasses} bg-white border-primary text-primary ring-4 ring-primary/20`;
      case StepStatus.Pending:
      default:
        return `${baseClasses} bg-white border-gray-300`;
    }
  }, []);

  const getConnectorClasses = useCallback((step: SubscriptionStepDto) => {
    const baseClasses = "grow border-b-2 border-b transition-all duration-300";
    const isConnectorCompleted = step.status === StepStatus.Completed;

    return isConnectorCompleted
      ? `${baseClasses} border-primary border-solid`
      : `${baseClasses} border-gray-400 border-dotted`;
  }, []);

  const isStepDisabled = useCallback((step: SubscriptionStepDto) => {
    if (isProcessCompleted) return true;
    // Dès qu'un devis emprunteur existe : seule la contractualisation reste accessible.
    if (isBorrowerNavigationLocked && step.identifier !== StepIdentifier.Contractualization) {
      return true;
    }
    return step.status === StepStatus.Pending;
  }, [isProcessCompleted, isBorrowerNavigationLocked]);

  // Suivi du projet pour le garde de redirection : pendant un changement de simulation,
  // contractualizationData appartient encore à l'ANCIEN projet (pas encore reset/rechargé).
  const prevGuardProjectIdRef = useRef<string | undefined>(borrowerProjectId);

  // Redirection de sécurité : si un devis emprunteur existe et que l'utilisateur est arrivé
  // sur une autre étape (URL directe, bouton "précédent"...), on le ramène sur la
  // contractualisation du devis concerné.
  useEffect(() => {
    const projectChanged = prevGuardProjectIdRef.current !== borrowerProjectId;
    prevGuardProjectIdRef.current = borrowerProjectId;

    // On NE redirige PAS pendant un changement de simulation (nouveau projet) ni pendant un
    // chargement : les données de contractualisation peuvent être périmées, ce qui ferait
    // bloquer à tort la création d'une nouvelle simulation (qui démarre sur "Création du dossier").
    if (projectChanged || isLoading) return;

    if (!isBorrowerNavigationLocked || isOnContractualisation) return;

    const empData = contractualizationData as
      | ({ type: 'EMP' } & BorrowerContractualizationResponse)
      | undefined;
    const targetQuoteId = quoteId ?? empData?.quoteId;
    const targetBorrowerId =
      borrowerId ??
      empData?.quoteTabs?.find(tab => tab.quoteId === targetQuoteId)?.borrowerId ??
      empData?.quoteTabs?.[0]?.borrowerId;

    if (!borrowerProjectId || !targetQuoteId || !targetBorrowerId) return;

    const config = getStepRouteByStepIdentifierString(
      BranchCode.Borrower,
      StepIdentifier.Contractualization
    );
    if (!config) return;

    try {
      const route = buildRoute(config.route, {
        borrowerProjectId,
        borrowerId: targetBorrowerId,
        quoteId: targetQuoteId
      });
      navigate(route, { replace: true });
    } catch {
      // Paramètres insuffisants pour construire la route : on ne redirige pas.
    }
  }, [
    isBorrowerNavigationLocked,
    isOnContractualisation,
    isLoading,
    contractualizationData,
    borrowerProjectId,
    borrowerId,
    quoteId,
    navigate
  ]);

  // Build insured options for NoteButton from riskData or contractualizationData
  const borrowerInsuredOptions = useMemo((): InsuredOption[] | undefined => {
    if (branchCode !== BranchCode.Borrower) return undefined;

    // Priority 1: riskData.insureds
    if (borrowerRiskData?.insureds && borrowerRiskData.insureds.length > 0) {
      return borrowerRiskData.insureds.map((insured) => ({
        borrowerId: insured.borrowerId,
        label: insured.type === 'physique'
          ? `${insured.firstName} ${insured.lastName}`
          : insured.companyName,
      }));
    }

    // Priority 2: contractualizationData.quoteTabs
    if (contractualizationData?.type === 'EMP') {
      const empData = contractualizationData as { type: 'EMP' } & BorrowerContractualizationResponse;
      if (empData.quoteTabs && empData.quoteTabs.length > 0) {
        return empData.quoteTabs.map((tab) => ({
          borrowerId: tab.borrowerId,
          label: tab.borrowerName || tab.label,
        }));
      }
    }

    return undefined;
  }, [branchCode, borrowerRiskData, contractualizationData]);

  if (isLoading || !branchCode) {
    return <SubscriptionProcessSkeleton />;
  }

  return (
    <div className='w-full mt-8'>

      {/* Header : titre centré, actions sur une seule rangée. Sur écran étroit
          les actions passent sous le titre (flex-col) et peuvent wrapper ;
          sur grand écran (xl) les flex-1 de part et d'autre gardent le titre
          centré avec les actions alignées à droite. */}
      <div className="mb-6 px-4">
        <div className="flex flex-col items-center gap-4 xl:flex-row max-w-6xl mx-auto">
          {/* Left spacer for balance */}
          <div className="hidden xl:block xl:flex-1" />

          {/* Center - Title and simulation number */}
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-zinc-800 mb-2">
              {t(getTitleByBranchCode(branchCode))}
            </h1>
            <div className='flex justify-center font-mono gap-2'>
              <p className='font-bold text-zinc-800'>
                {t('SUBSCRIPTION_PROCESS.TITLE.SIMULATION_NUMBER')}
              </p>
              <p className='text-gray-500'>
                {folderIdentifier}
              </p>
            </div>
          </div>

          {/* Actions. Emprunteur : 4 boutons en grille 2×2, colonnes en
              minmax(max-content,1fr) pour qu'elles s'ajustent au contenu sans
              jamais se compresser (les libellés débordaient avec grid-cols-2).
              Autres produits : simple ligne Historique / Note. */}
          <div className="xl:flex-1 flex justify-center xl:justify-end">
            {(riskId || (branchCode === BranchCode.Borrower && borrowerProjectId)) && (
              <div
                className={
                  branchCode === BranchCode.Borrower
                    ? 'grid grid-cols-[repeat(2,minmax(max-content,1fr))] gap-2'
                    : 'flex items-center gap-2'
                }
              >
                <TimelineButton
                  riskId={branchCode === BranchCode.Borrower ? undefined : riskId}
                  borrowerProjectId={branchCode === BranchCode.Borrower ? borrowerProjectId : undefined}
                  unreadCount={unreadHistoryCount}
                  // Ouvrir l'historique = tout lu : badge à 0 immédiatement, sans refresh
                  // (indispensable côté emprunteur, où SignalR n'est pas connecté).
                  onOpen={() => setUnreadHistoryCount(0)}
                  className={branchCode === BranchCode.Borrower ? 'w-full' : undefined}
                />
                <NoteButton
                  riskId={riskId}
                  insureds={borrowerInsuredOptions}
                  isBorrower={branchCode === BranchCode.Borrower}
                  className={branchCode === BranchCode.Borrower ? 'w-full' : undefined}
                />
                {branchCode === BranchCode.Borrower && <ReferenceDocumentButtons />}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-start justify-between w-1/2 mx-auto max-w-screen-md">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            {/* Cercle avec numéro et titre */}
            <div className="flex flex-col w-3 h-3 relative items-center ">
              <div className={getStepClasses(step.status)}>
              </div>
              {/* Titre de l'étape */}
              <StepRouteSelectorTooltip
                disabled={isStepDisabled(step)}
                routeConfigs={getStepRouteConfigs(step, branchCode)}
                className={`${step.status === StepStatus.Current ? 'font-medium' : ''}`}
                label={t(step.name)}
                substeps={step.subSteps}
              />
            </div>
            {/* Connecteur entre les étapes (sauf pour la dernière) */}
            {index < steps.length - 1 && (
              <div className={`${getConnectorClasses(step)} mt-[5px]`} />
            )}
          </React.Fragment>
        ))}
      </div>
      <div className='pt-2'>
        <Outlet />
      </div>
    </div>
  );
};

// Helper to get title by branch code
const getTitleByBranchCode = (branchCode: BranchCode): string => {
  switch (branchCode) {
    case BranchCode.Borrower:
      return 'SUBSCRIPTION_PROCESS.TITLE.CREATE_BORROWER_PROJECT';
    case BranchCode.UPI:
      return 'SUBSCRIPTION_PROCESS.TITLE.CREATE_UPI_PROJECT';
    case BranchCode.Unemployment:
      return 'SUBSCRIPTION_PROCESS.TITLE.CREATE_UNEMPLOYMENT_PROJECT';
    case BranchCode.RGI:
      return 'SUBSCRIPTION_PROCESS.TITLE.CREATE_RGI_PROJECT';
    case BranchCode.Mri:
      return 'SUBSCRIPTION_PROCESS.TITLE.CREATE_MRI_PROJECT';
    case BranchCode.LegalProtection:
      return 'SUBSCRIPTION_PROCESS.TITLE.CREATE_LEGAL_PROTECTION_PROJECT';
    default:
      return 'SUBSCRIPTION_PROCESS.TITLE.CREATE_PROJECT';
  }
};
